import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ITireTreadModel, defaultValue } from 'app/shared/model/tire-tread-model.model';

const initialState: EntityState<ITireTreadModel> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/tire-tread-models';

interface IPagedTireTreadModelQueryParams extends IQueryParams {
  nameSearch?: string;
}

// Actions

export const getEntities = createAsyncThunk('tireTreadModel/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITireTreadModel[]>(requestUrl);
});

export const getEntitiesByNameSearch = createAsyncThunk('tireTreadModel/fetch_entity_list', async ({ page, size, sort, nameSearch }: IPagedTireTreadModelQueryParams) => {
  const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : ''}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITireTreadModel[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'tireTreadModel/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITireTreadModel>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'tireTreadModel/create_entity',
  async (entity: ITireTreadModel) => {
    const result = await axios.post<ITireTreadModel>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'tireTreadModel/update_entity',
  async (entity: ITireTreadModel) => {
    const result = await axios.put<ITireTreadModel>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'tireTreadModel/partial_update_entity',
  async (entity: ITireTreadModel) => {
    const result = await axios.patch<ITireTreadModel>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'tireTreadModel/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITireTreadModel>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TireTreadModelSlice = createEntitySlice({
  name: 'tireTreadModel',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesByNameSearch), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntitiesByNameSearch), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TireTreadModelSlice.actions;

// Reducer
export default TireTreadModelSlice.reducer;
